import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

// ---------------------------------------------------------------------------
// Types — mirror server-nest/src/modules/unclaimed/unclaimed.types.ts
// (kept locally so we don't have to publish the server types via /shared).
// ---------------------------------------------------------------------------

export interface AcknowledgementStatus {
  acknowledged: boolean;
  termsVersion: string;
  videoId: string;
  acceptedAt?: string;
}

export type UnclaimedSortBy =
  | 'primaryTraveler'
  | 'supplierName'
  | 'travelType'
  | 'confirmationNumber'
  | 'departureDate'
  | 'returnDate'
  | 'vendorPaidDate'
  | 'issueDate'
  | 'totalFare'
  | 'commissionAmount';

export type UnclaimedSortDir = 'asc' | 'desc';

export type UnclaimedClaimStatus =
  | 'available'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

export interface UnclaimedInvoiceRow {
  tramsInvoiceNo: number;
  tramsBookingNo: number;
  tramsAgentBkgNo: number;
  recordLocator: string | null;
  agencyLinkNo: number | null;
  branchLinkNo: number | null;
  /** Placeholder client TRAMS bills the unclaimed pool to (same on every row). */
  clientLinkNo: number | null;
  primaryTraveler: string | null;
  supplierLinkNo: number | null;
  supplierName: string | null;
  travelType: string | null;
  confirmationNumber: string | null;
  departureDate: string | null;
  returnDate: string | null;
  totalFare: string | null;
  commissionAmount: string | null;
  vendorPaidDate: string | null;
  issueDate: string | null;
  invoiceStatusCode: string | null;
  claimStatus: UnclaimedClaimStatus;
  claimedByUserId: number | null;
}

export interface UnclaimedListResponse {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  asOf: string;
  /** Inclusive lower bound of the rolling cutoff window. */
  cutoffFrom: string;
  /** Inclusive upper bound of the rolling cutoff window (= asOf). */
  cutoffTo: string;
  records: UnclaimedInvoiceRow[];
}

export interface UnclaimedListParams {
  page?: number;
  pageSize?: number;
  sortBy?: UnclaimedSortBy;
  sortDir?: UnclaimedSortDir;
  asOf?: string;
  primaryTraveler?: string;
  supplier?: string;
  travelType?: string;
  confirmationNumber?: string;
  departureDateFrom?: string;
  departureDateTo?: string;
  returnDateFrom?: string;
  returnDateTo?: string;
  vendorPaidDateFrom?: string;
  vendorPaidDateTo?: string;
  issueDateFrom?: string;
  issueDateTo?: string;
}

export interface SearchableInvoiceRow {
  id: number;
  invoiceNumber: string | null;
  confirmationNumber: string | null;
  primaryTraveler: string | null;
  supplierName: string | null;
  totalFare: string | null;
  recordLocator: string | null;
  tripId: number;
  tripName: string | null;
  invoiceStatusCode: string | null;
}

export interface SearchableInvoicesResponse {
  items: SearchableInvoiceRow[];
  total: number;
}

export type SubmitClaimTarget =
  | { kind: 'existing'; invoiceId: number }
  | {
      kind: 'new';
      tripId: number;
      prepopulatedFields?: Record<string, unknown>;
      /**
       * Set when the wizard already created the AIVIA invoice via the
       * standard create-invoice flow (`POST /api/trips/with-invoices`).
       * The server validates ownership + tripId match and reuses it
       * instead of building a new invoice from the unclaimed snapshot.
       */
      prepopulatedInvoiceId?: number;
    };

export interface SubmitClaimInput {
  /**
   * Composite booking-line key. Required because the unclaimed pool stores
   * multiple booking lines under one monthly TRAMS invoice — see
   * `docs/unclaimed-invoices.md`.
   */
  tramsInvoiceNo: number;
  tramsBookingNo: number;
  tramsAgentBkgNo: number;
  /**
   * The exact row the user clicked. The server stores this verbatim as
   * `payloadSnapshot` for audit/email and uses our composite key for
   * routing.
   */
  rowSnapshot: UnclaimedInvoiceRow;
  target: SubmitClaimTarget;
  idempotencyKey: string;
  asOf?: string;
  /** Optional note to Member Success, included in the support email. */
  note?: string;
}

export interface SubmitClaimResponse {
  claimId: number;
  status: string;
  targetKind: 'existing' | 'new';
  targetInvoiceId: number | null;
  targetInvoiceNumber: string | null;
  supportEmailSentAt: string | null;
  replayed: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const unclaimedKeys = {
  acknowledgement: ['unclaimed', 'acknowledgement'] as const,
  list: (params: UnclaimedListParams) =>
    ['unclaimed', 'list', params] as const,
  travelTypes: ['unclaimed', 'travelTypes'] as const,
  searchable: (q: string) =>
    ['unclaimed', 'searchable', q] as const,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Drop all unclaimed React Query cache (logout / user switch). */
export function clearUnclaimedClientCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: ['unclaimed'] });
}

function buildQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** GET /api/unclaimed/acknowledgement — gate status. */
export function useUnclaimedAcknowledgement() {
  return useQuery<AcknowledgementStatus>({
    queryKey: unclaimedKeys.acknowledgement,
    queryFn: () => apiRequest('GET', '/api/unclaimed/acknowledgement'),
    staleTime: 60_000,
  });
}

/** POST /api/unclaimed/acknowledgement — record video watched + accept terms. */
export function useRecordAcknowledgement() {
  const queryClient = useQueryClient();
  return useMutation<
    AcknowledgementStatus,
    Error,
    { videoWatchedAt: Date; acceptedTerms?: boolean }
  >({
    mutationFn: (input) => {
      // Acceptance of the terms of use must be an explicit user action.
      // Never silently assume consent — the server is the source of truth
      // for the gate but we also refuse to forward the request without it.
      if (input.acceptedTerms === false) {
        return Promise.reject(
          new Error('Terms must be accepted before acknowledgement is recorded.'),
        );
      }
      return apiRequest('POST', '/api/unclaimed/acknowledgement', {
        videoWatchedAt: input.videoWatchedAt.toISOString(),
        ...(input.acceptedTerms === undefined
          ? {}
          : { acceptedTerms: input.acceptedTerms }),
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(unclaimedKeys.acknowledgement, data);
      queryClient.invalidateQueries({
        queryKey: ['unclaimed', 'list'],
        exact: false,
      });
    },
  });
}

/** GET /api/unclaimed/invoices — paginated/sorted/filtered list. */
export function useUnclaimedInvoices(
  params: UnclaimedListParams,
  enabled = true,
) {
  return useQuery<UnclaimedListResponse>({
    queryKey: unclaimedKeys.list(params),
    queryFn: () =>
      apiRequest(
        'GET',
        `/api/unclaimed/invoices${buildQueryString(params as Record<string, unknown>)}`,
      ),
    enabled,
    placeholderData: (previous) => previous,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/** Distinct travel types from the unclaimed pool (for filter dropdown). */
export function useUnclaimedTravelTypes(enabled = true) {
  return useQuery<string[]>({
    queryKey: unclaimedKeys.travelTypes,
    queryFn: () => apiRequest('GET', '/api/unclaimed/travel-types'),
    enabled,
    staleTime: 60 * 60_000,
  });
}

/** Default table order — the third tap on a sorted header returns here (AR-412). */
export const DEFAULT_UNCLAIMED_SORT: { sortBy: UnclaimedSortBy; sortDir: UnclaimedSortDir } = {
  sortBy: 'departureDate',
  sortDir: 'desc',
};

const DEFAULT_UNCLAIMED_LIST_PARAMS: UnclaimedListParams = {
  page: 1,
  pageSize: 25,
  sortBy: 'departureDate',
  sortDir: 'desc',
};

/** Prefetch the default first page (shared pool cache on the server). */
export function prefetchUnclaimedInvoices(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return queryClient.prefetchQuery({
    queryKey: unclaimedKeys.list(DEFAULT_UNCLAIMED_LIST_PARAMS),
    queryFn: () =>
      apiRequest(
        'GET',
        `/api/unclaimed/invoices${buildQueryString(DEFAULT_UNCLAIMED_LIST_PARAMS as Record<string, unknown>)}`,
      ),
    staleTime: 60_000,
  });
}

export function prefetchUnclaimedTravelTypes(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return queryClient.prefetchQuery({
    queryKey: unclaimedKeys.travelTypes,
    queryFn: () => apiRequest('GET', '/api/unclaimed/travel-types'),
    staleTime: 60 * 60_000,
  });
}

/** Warm list + filter metadata (one shared Lambda pool load on the server). */
export function prefetchUnclaimedPageData(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return Promise.all([
    prefetchUnclaimedInvoices(queryClient),
    prefetchUnclaimedTravelTypes(queryClient),
  ]);
}

/** GET /api/unclaimed/invoices/searchable — wizard "find existing invoice" search. */
export function useSearchableInvoices(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery<SearchableInvoicesResponse>({
    queryKey: unclaimedKeys.searchable(trimmed),
    queryFn: () =>
      apiRequest(
        'GET',
        `/api/unclaimed/invoices/searchable?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled: enabled && trimmed.length >= 2,
    staleTime: 15_000,
  });
}

/** POST /api/unclaimed/claims — submit a claim. Idempotent on `idempotencyKey`. */
export function useSubmitClaim() {
  const queryClient = useQueryClient();
  return useMutation<SubmitClaimResponse, Error, SubmitClaimInput>({
    mutationFn: (input) =>
      apiRequest('POST', '/api/unclaimed/claims', input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['unclaimed', 'list'],
        exact: false,
      });
    },
  });
}
